// src/pages/HomePage.tsx
import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PackageLoader from '../components/PackageLoader';
import { motion } from 'framer-motion';
import { MapPin, Car, ExternalLink, ArrowRight, Sparkles, Check } from 'lucide-react';
import Seo from '../components/Seo';

type Destination = {
  id: string;
  name: string;
  description: string;
  image: string;
  highlights: string[];
  location: string;
};

const destinations: Destination[] = [
  {
    id: '1',
    name: 'Petra',
    description:
      'The ancient Nabataean city carved into rose-red sandstone cliffs, one of the New Seven Wonders of the World.',
    image:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Treasury_petra_crop.jpeg/330px-Treasury_petra_crop.jpeg',
    highlights: ['The Treasury', 'Monastery', 'Royal Tombs', 'Siq Canyon'],
    location: "Ma'an Governorate",
  },
  {
    id: '2',
    name: 'Wadi Rum',
    description:
      'The Valley of the Moon - a protected desert wilderness with dramatic sandstone mountains and red sand valleys.',
    image: 'https://pictures.altai-travel.com/1920x0/petra-to-wadi-rum-header-adobe-stock-3433.jpg',
    highlights: ['Desert Safari', 'Rock Bridges', 'Ancient Petroglyphs', 'Bedouin Culture'],
    location: 'Aqaba Governorate',
  },
  {
    id: '3',
    name: 'Aqaba',
    description:
      "Jordan's Red Sea jewel featuring world-class diving, vibrant marinas, and breezy seaside promenades.",
    image:
      'https://www.muchbetteradventures.com/magazine/content/images/2023/11/Aqaba_Red_Sea_Jordan_Canva-1.jpeg',
    highlights: ['Red Sea Diving', 'Glass-Bottom Cruises', 'Ayla Marina', 'Beachfront Cafes'],
    location: 'Aqaba Governorate',
  },
  {
    id: '4',
    name: 'Dead Sea',
    description:
      'The lowest point on Earth, famous for its therapeutic salt waters and mineral-rich mud.',
    image:
      'https://images.squarespace-cdn.com/content/v1/64ba44348b6a05559a816bc1/1690282697224-Q4RJU99FL0HYLW9KFZ3M/Jordan%2BDead%2BSea%2Bon%2Ba%2BBudget-title.jpg',
    highlights: ['Natural Floating', 'Mineral Mud', 'Spa Resorts', 'Stunning Sunsets'],
    location: 'Jordan Valley',
  },
  {
    id: '5',
    name: 'Amman',
    description:
      "Jordan's vibrant capital blending ancient history with modern culture, built on seven hills.",
    image:
      'https://i0.wp.com/www.touristjordan.com/wp-content/uploads/2022/08/jordan12.jpg?resize=800%2C533&ssl=1',
    highlights: ['Roman Theatre', 'Citadel Hill', 'Rainbow Street', 'Traditional Souks'],
    location: 'Capital City',
  },
];

const accentGradients = [
  'from-emerald-400/90 via-emerald-500/60 to-teal-500/50',
  'from-amber-400/90 via-orange-500/60 to-red-500/50',
  'from-cyan-400/90 via-blue-500/60 to-indigo-500/50',
  'from-rose-400/90 via-pink-500/60 to-amber-500/50',
];

const goldenTriangleDestinations = ['Petra', 'Wadi Rum', 'Aqaba'];

type WhyTravelReason = {
  icon: React.ElementType;
  title: string;
  description: string;
  accent: string;
};

const whyTravelReasons: WhyTravelReason[] = [
  {
    icon: Check,
    title: 'Culture First',
    description:
      'Unhurried days that keep Jordanian hospitality at the center—from Petra’s carved facades to the cafés and souks that locals love.',
    accent: 'from-rose-400/70 via-orange-300/50 to-amber-200/40',
  },
  {
    icon: Check,
    title: 'Respect for the Land',
    description:
      'Routes planned with the desert, canyons, and Red Sea reefs in mind, plus guidance on traveling lightly while you explore.',
    accent: 'from-emerald-400/70 via-teal-300/50 to-cyan-200/40',
  },
  {
    icon: Check,
    title: 'Pacing That Flows',
    description:
      'Balanced links between Petra, Wadi Rum, Aqaba, and the Dead Sea so you see more of Jordan without the rush.',
    accent: 'from-amber-400/70 via-orange-400/60 to-red-300/40',
  },
  {
    icon: Check,
    title: 'Clear Guidance',
    description:
      'Straightforward logistics, practical tips, and a team focused on keeping your journey smooth before and during the trip.',
    accent: 'from-cyan-400/70 via-blue-400/50 to-indigo-300/40',
  },
];

const DestinationCard: React.FC<{
  destination: Destination;
  index: number;
  isGoldenTriangle?: boolean;
}> = ({ destination, index, isGoldenTriangle = false }) => {
  const accent = accentGradients[index % accentGradients.length];

  return (
    <motion.article
      className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: 'easeOut' }}
      whileHover={{ scale: 1.01, y: -6 }}
    >
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-60 transition duration-300 bg-gradient-to-br ${accent} blur-3xl`} />
      <div className="absolute -inset-px rounded-3xl border border-white/5 group-hover:border-white/15 transition" />

      {/* Image */}
      <div className="relative h-64 sm:h-72 overflow-hidden">
        {isGoldenTriangle && (
          <div className="absolute top-4 right-4 z-10">
            <div className="relative">
              <div className="absolute inset-0 blur-xl bg-amber-400/35 animate-pulse" />
              <div className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-amber-50 text-[11px] font-semibold tracking-[0.18em] uppercase border border-amber-200/60 shadow-lg shadow-amber-500/30">
                <Sparkles className="w-4 h-4" />
                Golden Triangle
              </div>
            </div>
          </div>
        )}
        <motion.img
          src={destination.image}
          alt={destination.name}
          className="w-full h-full object-cover scale-105"
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />

        <div className="absolute top-4 left-4">
          <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-emerald-500/30">
            <MapPin className="w-4 h-4" />
            {destination.location}
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">{destination.name}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-7 space-y-5">
        <p className="text-base text-slate-200 leading-relaxed">
          {destination.description}
        </p>

        <div className="flex flex-wrap gap-2">
          {destination.highlights.map((highlight, i) => (
            <span
              key={`${destination.id}-highlight-${i}`}
              className="text-xs uppercase tracking-wide text-white/90 bg-white/10 border border-white/10 px-3 py-2 rounded-full backdrop-blur-sm"
            >
              {highlight}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <a href="/packages" className="btn-primary px-4 py-3 rounded-2xl text-sm font-bold">
            Explore Trips
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

const CarRentalPromotion: React.FC = () => {
  return (
    <div className="relative bg-gradient-to-br from-slate-900/80 via-amber-900/85 to-orange-900/80 rounded-3xl p-8 overflow-hidden border border-amber-700/40">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.5),transparent_70%)]" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-20" />
      </div>

      <div className="relative max-w-4xl mx-auto text-center">
        <motion.div
          className="inline-flex items-center gap-3 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 rounded-2xl">
            <Car className="w-8 h-8 text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-3xl font-bold text-white mb-1">Need a Ride?</h2>
            <p className="text-amber-200 text-sm">Powered by Save Rent A Car</p>
          </div>
        </motion.div>

        <motion.p
          className="text-xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Explore Jordan's famous destinations with freedom and comfort.{' '}
          <span className="text-cyan-300 font-semibold">Save Rent A Car</span> offers reliable vehicles
          to make your journey seamless from Amman to Petra and beyond.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <a
            href="https://saverentacar.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary group font-bold px-8 py-4 rounded-2xl"
          >
            <Car className="w-5 h-5" />
            <span>Browse Rental Cars</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>

          <div className="flex items-center gap-4 text-sm text-slate-300">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full" />
              <span>Best Prices</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full" />
              <span>Online Booking</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-purple-400 rounded-full" />
              <span>Diverse fleet</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-orange-900">
      <Seo
        title="Above & Below Adventures — Jordan Tours & Travel"
        description="Curated tours across Jordan: Petra, Wadi Rum, the Dead Sea and more. Flexible itineraries, local guides, and easy booking."
        canonicalPath="/"
        image="/assets/jordan.png"
        jsonLd={{
          '@type': 'Organization',
          name: 'Above & Below Adventures',
          url: import.meta.env.VITE_SITE_URL,
          logo: `${import.meta.env.VITE_SITE_URL}/assets/logo.png`,
          sameAs: [
            'https://www.facebook.com/aboveandbelow.info/',
            'https://www.tripadvisor.com/Attraction_Review-g298101-d7368530-Reviews-Above_and_Below_Adventures_Day_Tours-Aqaba_Al_Aqabah_Governorate.html',
            'https://www.instagram.com/above_and_below_adventures/?igsh=MW9keDg1enE3amxtbg%3D%3D#',
          ],
        }}
      />

      <Header isOverlay={true} />

      {/* HERO */}
      <section
        className="relative h-screen overflow-hidden bg-slate-900"
      >
        <video
          className="absolute inset-0 w-full h-full object-cover scale-110"
          src="/assets/Jordan.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute inset-0 bg-black/25" />
      </section>

      {/* Famous Destinations */}
      <section
        id="famous-destinations"
        className="container mx-auto px-6 py-16 sm:py-20 lg:py-24"
      >
        <div className="max-w-5xl mx-auto text-center mb-12 lg:mb-16">
          <motion.h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-white via-amber-200 to-orange-200 bg-clip-text text-transparent drop-shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Famous Destinations
          </motion.h2>
          <motion.p
            className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Discover Jordan&apos;s most iconic locations that have captivated travelers for centuries.
            Each destination offers unique experiences and unforgettable memories.
          </motion.p>
        </div>

        <motion.div
          className="relative overflow-hidden mb-12 lg:mb-14 rounded-3xl border border-amber-300/20 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-orange-500/10 px-5 py-6 sm:px-6 sm:py-7 shadow-[0_30px_80px_-40px_rgba(255,193,94,0.8)]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.35, ease: 'easeOut' }}
        >
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="absolute -left-12 -bottom-12 h-28 w-28 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-gradient-to-br from-amber-500/40 via-orange-500/40 to-amber-400/50 border border-amber-200/60 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Sparkles className="w-6 h-6 text-amber-50" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-amber-100">
                  Golden Triangle
                </p>
                <p className="text-base sm:text-lg text-amber-50/90">
                  Petra, Wadi Rum, and Aqaba are Jordan&apos;s famed "Golden Triangle" - a perfect arc of
                  wonder, desert skies, and Red Sea sunsets.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {goldenTriangleDestinations.map((stop) => (
                <span
                  key={stop}
                  className="inline-flex items-center gap-2 rounded-full border border-amber-200/40 bg-amber-500/15 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-50 shadow-md shadow-amber-500/20 backdrop-blur-sm"
                >
                  <span className="h-2 w-2 rounded-full bg-amber-300 shadow-[0_0_0_4px_rgba(251,191,36,0.15)]" />
                  {stop}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-10 auto-rows-[1fr]">
          {loading
            ? ['ph-0', 'ph-1', 'ph-2', 'ph-3', 'ph-4', 'ph-5'].map((pid) => (
              <PackageLoader key={pid} />
            ))
            : destinations.map((destination, index) => (
              <DestinationCard
                key={destination.id}
                destination={destination}
                index={index}
                isGoldenTriangle={goldenTriangleDestinations.includes(destination.name)}
              />
            ))}
        </div>
      </section>

      {/* Why Jordan */}
      <section className="relative py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto mb-12 lg:mb-16">
            <motion.h2
              className="text-4xl sm:text-5xl font-bold mb-4 text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Why Travel with Us in Jordan?
            </motion.h2>
            <motion.p
              className="text-xl text-slate-300 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              We blend authenticity, sustainability, and local insight so your journey feels personal
              and unforgettable.
            </motion.p>
          </div>
          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 gap-y-14 gap-x-12">
            {whyTravelReasons.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="relative flex gap-4 sm:gap-5 items-start"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  <div className="relative shrink-0">
                    <div className={`absolute inset-[-16px] blur-2xl opacity-60 bg-gradient-to-br ${item.accent}`} />
                    <div className="relative h-12 w-12 rounded-full border border-white/15 bg-slate-950/70 backdrop-blur flex items-center justify-center shadow-lg shadow-amber-500/10">
                      <Icon className="w-6 h-6 text-amber-50" />
                    </div>
                  </div>
                  <div className="space-y-2 max-w-md">
                    <h3 className="font-bold text-2xl text-white">{item.title}</h3>
                    <p className="text-slate-300 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Car Rental Partnership */}
      <section className="container mx-auto px-6 py-16 sm:py-20 lg:py-24">
        <CarRentalPromotion />
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
